import type { TertiaryEntityFailure } from '@/composables/useTertiaryEntityWorkflow';

/** Departments queued under one faculty in the tertiary board's batch form. */
export interface DepartmentBatchGroup {
  facultyId: string;
  facultyName: string;
  type: string;
  departments: string[];
  /** Why a department was not created on the last submit, keyed by its name. */
  errors?: Record<string, string>;
}

export interface DepartmentBatchItem {
  groupIndex: number;
  dept: string;
  facultyId: string;
  type: string;
}

export function flattenDepartmentBatch(groups: DepartmentBatchGroup[]): DepartmentBatchItem[] {
  return groups.flatMap((group, groupIndex) =>
    group.departments.map(dept => ({ groupIndex, dept, facultyId: group.facultyId, type: group.type })),
  );
}

/**
 * The batch after a submit: only the departments that failed remain, each
 * with its reason, and faculties left with none drop out.
 */
export function keepFailedDepartments(
  groups: DepartmentBatchGroup[],
  failures: TertiaryEntityFailure<DepartmentBatchItem>[],
): DepartmentBatchGroup[] {
  return groups
    .map((group, groupIndex) => {
      const failed = failures.filter(failure => failure.item.groupIndex === groupIndex);
      return {
        ...group,
        departments: failed.map(failure => failure.item.dept),
        errors: Object.fromEntries(failed.map(failure => [failure.item.dept, failure.message])),
      };
    })
    .filter(group => group.departments.length > 0);
}
